/**
 * @copyright WaterStreet. All rights reserved.
 */

import {
	ChangeDetectorRef,
	Component,
	HostListener,
	OnInit
} from '@angular/core';
import {
	ExtendedCustomControlDirective
} from '@entity/directives/extended-custom-control.directive';
import {
	AppEventConstants
} from '@shared/constants/app-event.constants';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	DocumentHelper
} from '@shared/helpers/document.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	IMonacoEditorOptions
} from '@shared/interfaces/application-objects/monaco-editor-options';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';
import {
	debounceTime,
	Subject
} from 'rxjs';

@Component({
	selector: 'custom-code-editor',
	templateUrl: './custom-code-editor.component.html',
	styleUrls: [
		'./custom-code-editor.component.scss'
	]
})

/**
 * A component representing an instance of a code editor based text area.
 *
 * @export
 * @class CustomCodeEditorComponent
 * @extends {ExtendedCustomControlDirective}
 * @implements {OnInit}
 */
export class CustomCodeEditorComponent
	extends ExtendedCustomControlDirective
	implements OnInit
{
	/**
	 * Initializes a new instance of the CustomCodeEditorComponent.
	 *
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used in this component.
	 * @param {ChangeDetectorRef} changeDetector
	 * The change detector reference for this component.
	 * @memberof CustomCodeEditorComponent
	 */
	public constructor(
		public siteLayoutService: SiteLayoutService,
		public changeDetector: ChangeDetectorRef)
	{
		super(
			changeDetector);
	}

	/**
	 * Gets or sets the layout changed subject which is called
	 * when the site layout sizes are changed in this component.
	 *
	 * @type {Subject<void>}
	 * @memberof CustomCodeEditorComponent
	 */
	public layoutChanged: Subject<void> = new Subject<void>();

	/**
	 * Gets or sets the value that signifies whether the code editor should
	 * use available screen height.
	 *
	 * @type {boolean}
	 * @memberof CustomCodeEditorComponent
	 */
	public fullPageView: boolean = false;

	/**
	 * Gets or sets the text display value.
	 *
	 * @type {string}
	 * @memberof CustomCodeEditorComponent
	 */
	public textDisplay: string = AppConstants.empty;

	/**
	 * Gets or sets a value indicating whether the text is valid.
	 *
	 * @type {boolean}
	 * @memberof CustomCodeEditorComponent
	 */
	public isValidText: boolean = true;

	/**
	 * Gets or sets the editor height as a string in pixels.
	 *
	 * @type {string}
	 * @memberof CustomCodeEditorComponent
	 */
	public editorHeight: string = '96.5px';

	/**
	 * Gets or sets the fields disabled value.
	 *
	 * @type {boolean}
	 * @memberof CustomCodeEditorComponent
	 */
	public disabled: boolean = false;

	/**
	 * Gets or sets the editor options.
	 *
	 * @type {IMonacoEditorOptions}
	 * @memberof CustomCodeEditorComponent
	 */
	public editorOptions: IMonacoEditorOptions =
		<IMonacoEditorOptions>
		{
			// Configurable properties.
			theme: 'vs-light',
			language: AppConstants.markdownLanguages.json,
			lineNumbers: 'off',
			lineCount: 5,

			// Application defaults.
			automaticLayout: true,
			fontFamily: AppConstants.cssValues.fontFamily,
			fontSize: AppConstants.staticLayoutSizes.fontSize,
			fontLigatures: true,
			tabSize: AppConstants.jsonTabIndent,
			insertSpaces: false,

			// Field disabled property.
			readOnly: false
		};

	/**
	 * Gets or sets the height in pixels of a line in the editor.
	 *
	 * @type {number}
	 * @memberof CustomCodeEditorComponent
	 */
	private readonly lineCountHeight: number = 20;

	/**
	 * Handles the site layout change event which is called
	 * when the site layout service has altered it's variables
	 * or loading has completed.
	 *
	 * @memberof CustomCodeEditorComponent
	 */
	@HostListener(
		AppEventConstants.siteLayoutChangedEvent)
	public siteLayoutChanged(): void
	{
		this.layoutChanged.next();
	}

	/**
	 * Handles the on initialization event.
	 * This will set the JSON display value from the model and handle
	 * customizations.
	 *
	 * @memberof CustomCodeEditorComponent
	 */
	public ngOnInit(): void
	{
		this.field.props.isValidJson =
			this.isValidJson.bind(this);

		this.editorOptions.theme =
			this.field.props?.theme
				?? this.editorOptions.theme;
		this.editorOptions.language =
			this.field.props?.language
				?? this.editorOptions.language;
		this.editorOptions.lineNumbers =
			this.field.props?.lineNumbers
				?? this.editorOptions.lineNumbers;
		this.editorOptions.lineCount =
			this.field.props?.lineCount
				?? this.editorOptions.lineCount;
		this.fullPageView =
			this.field.props?.fullPageView
				?? false;

		this.disabled =
			this.field.props?.disabled
				?? false;
		if (this.disabled === true)
		{
			this.editorOptions.readOnly = true;
		}

		this.layoutChanged.pipe(
			debounceTime(this.siteLayoutService.debounceDelay))
			.subscribe(
				() =>
				{
					this.setEditorHeight();
				});

		this.layoutChanged.next();

		if (this.editorOptions.language !==
			AppConstants.markdownLanguages.json)
		{
			this.textDisplay =
				this.field.formControl.value;

			return;
		}

		this.textDisplay =
			typeof this.field.formControl.value ===
				AppConstants.propertyTypes.string
				? this.field.formControl.value
				: JSON.stringify(
					this.field.formControl.value,
					null,
					AppConstants.jsonTabIndent)
					?.trim();
	}

	/**
	 * Handles the on destroy event.
	 * This will unsusbcribe from the layout changed event.
	 *
	 * @memberof CustomCodeEditorComponent
	 */
	public ngOnDestroy(): void
	{
		this.layoutChanged.unsubscribe();
	}

	/**
	 * Validates the control.
	 *
	 * @returns {boolean}
	 * A value indicating whether Json is the text type and the Json is valid.
	 * @memberof CustomCodeEditorComponent
	 */
	public isValidJson(): boolean
	{
		return this.editorOptions.language ===
			AppConstants.markdownLanguages.json
			&& this.isValidText;
	}

	/**
	 * Handles the editor change event.
	 * This will set the JSON validity.
	 *
	 * @param {string} event
	 * The event that triggered the change.
	 * @memberof CustomCodeEditorComponent
	 */
	public editorChange(
		event: string): void
	{
		switch (this.editorOptions.language)
		{
			case AppConstants.markdownLanguages.json:
				this.isValidText =
					StringHelper.isValidJson(
						this.textDisplay);
				this.field.formControl.setValue(
					this.isValidText
						? JSON.parse(this.textDisplay)
						: null);
				break;

			case AppConstants.markdownLanguages.csharp:
				this.isValidText = true;
				this.field.formControl.setValue(
					this.textDisplay);
				break;
		}

		this.validateControl();

		if (!AnyHelper.isNull(this.field.props.change))
		{
			this.field.props.change(
				this.field,
				event);
		}
	}

	/**
	 * Handles the editor height calculations.
	 * This will set the JSON validity.
	 *
	 * @memberof CustomCodeEditorComponent
	 */
	public setEditorHeight(): void
	{
		if (this.fullPageView !== true)
		{
			this.editorHeight =
				`${(this.editorOptions.lineCount * this.lineCountHeight)
					- AppConstants.staticLayoutSizes.smallPadding}px`;

			this.changeDetector.detectChanges();

			return;
		}

		const pageTitleHeight: number =
			Math.floor(DocumentHelper.getPageHeaderHeight() ?? 0);
		const reservedPageHeight: number =
			(AppConstants.staticLayoutSizes.nestedContentPadding * 4)
				+ pageTitleHeight;

		this.editorHeight =
			`${Math.floor(
				this.siteLayoutService.displayTabletView === true
					? this.siteLayoutService.windowHeight
						- AppConstants.staticLayoutSizes.mobileHeaderHeight
						- reservedPageHeight
					: this.siteLayoutService.windowHeight
						- reservedPageHeight)}px`;

		this.changeDetector.detectChanges();
	}
}